import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOOptions {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const DEFAULT_SEO = {
  title: 'ZpiritualZ Den Haag | Spirituele Webshop – Blue Protect & Evil Energy Destroy',
  description: 'ZpiritualZ in Den Haag – Premium spirituele beschermingsproducten. Blue Protect Serie (indigofera), Evil Energy Destroy Serie (hing/asafoetida) & ritueel blueprints. Bescherming tegen het boze oog & negatieve energie.',
  canonical: 'https://www.zpiritualz.com/',
  keywords: 'spirituele bescherming, boze oog, evil energy destroy, blue protect, hing, asafoetida, spirituele webshop Den Haag',
};

const setMeta = (selector: string, attrName: string, value: string) => {
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    const parts = selector.match(/\[([^\]]+)="([^\]]+)"\]/);
    if (parts) el.setAttribute(parts[1], parts[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, value);
};

const setCanonical = (href: string) => {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute('data-dynamic', 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const BASE_URL = 'https://www.zpiritualz.com';

const HREFLANG_LANGS: { hreflang: string; prefix: string }[] = [
  { hreflang: 'x-default', prefix: '' },
  { hreflang: 'nl', prefix: '/nl' },
  { hreflang: 'en', prefix: '/en' },
  { hreflang: 'es', prefix: '/es' },
];

const setHreflang = (canonical: string) => {
  // Remove existing dynamic hreflang tags
  document.querySelectorAll('link[data-hreflang="dynamic"]').forEach((el) => el.remove());

  // Extract the path from the canonical URL
  let path = '/';
  try {
    path = new URL(canonical).pathname;
  } catch {
    path = canonical.replace(BASE_URL, '') || '/';
  }

  HREFLANG_LANGS.forEach(({ hreflang, prefix }) => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'alternate');
    el.setAttribute('hreflang', hreflang);
    // x-default points to the canonical URL as-is (no lang prefix)
    el.setAttribute('href', hreflang === 'x-default' ? `${BASE_URL}${path}` : `${BASE_URL}${prefix}${path}`);
    el.setAttribute('data-hreflang', 'dynamic');
    document.head.appendChild(el);
  });
};

const removeHreflang = () => {
  document.querySelectorAll('link[data-hreflang="dynamic"]').forEach((el) => el.remove());
};

const JSON_LD_ID = 'zpiritualz-jsonld';

const setJsonLd = (data: Record<string, unknown> | Record<string, unknown>[]) => {
  let el = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = JSON_LD_ID;
    el.setAttribute('type', 'application/ld+json');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = () => {
  const el = document.getElementById(JSON_LD_ID);
  if (el) el.remove();
};

export const useNoIndex = () => {
  useEffect(() => {
    let el = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (el) el.setAttribute('content', 'index, follow');
    };
  }, []);
};

export const useSEO = (options: SEOOptions) => {
  const location = useLocation();

  useEffect(() => {
    const { title, description, canonical, keywords, ogImage, jsonLd } = options;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[name="robots"]', 'content', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('meta[name="Last-modified"]', 'content', new Date().toISOString().split('T')[0]);
    setCanonical(canonical);

    // Keywords
    if (keywords) {
      setMeta('meta[name="keywords"]', 'content', keywords);
    }

    // Open Graph
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:site_name"]', 'content', 'ZpiritualZ');
    setMeta('meta[property="og:locale"]', 'content', 'nl_NL');
    if (ogImage) {
      setMeta('meta[property="og:image"]', 'content', ogImage);
      setMeta('meta[property="og:image:width"]', 'content', '1200');
      setMeta('meta[property="og:image:height"]', 'content', '630');
    }

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    if (ogImage) {
      setMeta('meta[name="twitter:image"]', 'content', ogImage);
    }

    // JSON-LD structured data
    if (jsonLd) {
      setJsonLd(jsonLd);
    }

    // Hreflang — inject dynamic <link rel="alternate"> tags per page
    setHreflang(canonical);

    return () => {
      document.title = DEFAULT_SEO.title;
      setMeta('meta[name="description"]', 'content', DEFAULT_SEO.description);
      setMeta('meta[name="robots"]', 'content', 'index, follow');
      setMeta('meta[name="keywords"]', 'content', DEFAULT_SEO.keywords);
      setCanonical(DEFAULT_SEO.canonical);
      setMeta('meta[property="og:title"]', 'content', DEFAULT_SEO.title);
      setMeta('meta[property="og:description"]', 'content', DEFAULT_SEO.description);
      setMeta('meta[property="og:url"]', 'content', DEFAULT_SEO.canonical);
      setMeta('meta[property="og:type"]', 'content', 'website');
      setMeta('meta[property="og:site_name"]', 'content', 'ZpiritualZ');
      setMeta('meta[name="twitter:title"]', 'content', DEFAULT_SEO.title);
      setMeta('meta[name="twitter:description"]', 'content', DEFAULT_SEO.description);
      removeJsonLd();
      removeHreflang();
    };
  }, [options.title, options.description, options.canonical, options.keywords, options.ogImage, options.jsonLd, location.pathname]);
};
